import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    List,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    MenuItem,
    Radio,
    Select,
    Stack,
    Typography,
    Divider,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Urs } from "../UrsSlice";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import { selectBlobsByResourceId } from "../../documents/attachementsSlice";
import {
    generateUrsTemplateAIAgent,
    GenerateUrsTemplatePayload,
    prepareUrsTemplateAIAgent,
} from "../api/ursTemplateApi";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { GenerateButton } from "../../../app/components/GenerateButton";
import { CancelButton } from "../../../app/components/CancelButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

const REQUIREMENT_TYPES = [
    { value: "URS-F", label: "URS-F — Funkcjonalne" },
    { value: "URS-S", label: "URS-S — Bezpieczeństwa" },
    { value: "URS-D", label: "URS-D — Projektowe" },
    { value: "URS-C", label: "URS-C — Sterowania" },
    { value: "URS-A", label: "URS-A — Dodatkowe" },
    { value: "URS-U", label: "URS-U — Media" },
    { value: "URS-R", label: "URS-R — Regulacyjne" },
    { value: "URS-DOC", label: "URS-DOC — Dokumentacja" },
    { value: "URS-E", label: "URS-E — Środowiskowe" },
];

export interface UrsTemplateAIFromFileGeneratorProps {
    ursTemplate: Urs;
    defaultTypeFilter?: string;
    onGenerate: (urs: Urs) => void;
    onCancel: () => void;
}

export const UrsTemplateAIFromFileGenerator = ({
    ursTemplate,
    defaultTypeFilter = "URS-F",
    onGenerate,
    onCancel,
}: UrsTemplateAIFromFileGeneratorProps) => {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");
    const [selectedBlobId, setSelectedBlobId] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<string>(defaultTypeFilter);
    const [language, setLanguage] = useState<"PL" | "EN">("PL");
    const [fileSelectionError, setFileSelectionError] = useState(false);
    type ProgressStage = "PREPARING" | "GENERATING" | null;
    const [progressStage, setProgressStage] = useState<ProgressStage>(null);

    const attachmentGroup = useAppSelector((state) =>
        selectBlobsByResourceId(state, ursTemplate.guid)
    );

    const blobs = attachmentGroup?.blobs ?? [];
    const hasBlobs = blobs.length > 0;

    const selectedBlob = useMemo(
        () => blobs.find((b) => b.blobId === selectedBlobId) ?? null,
        [blobs, selectedBlobId]
    );

    const fileIcon = (contentType?: string) => {
        if (contentType === "application/pdf") return <PictureAsPdfIcon />;
        return <InsertDriveFileOutlinedIcon />;
    };

    const handleConfirmGenerateClick = async () => {
        setSaving(true);
        setProgressStage(null);

        if (!selectedBlobId) {
            setFileSelectionError(true);
            setSaving(false);
            return;
        }

        try {
            setProgressStage("PREPARING");

            const prepareResult = await dispatch(
                prepareUrsTemplateAIAgent({
                    documentId: ursTemplate.id,
                    blobId: selectedBlobId,
                })
            );

            if (prepareUrsTemplateAIAgent.fulfilled.match(prepareResult)) {
                const payload: GenerateUrsTemplatePayload = {
                    documentId: ursTemplate.id,
                    fileId: selectedBlobId,
                    typeCode: typeFilter,
                    language,
                };

                setProgressStage("GENERATING");

                const genResult = await dispatch(generateUrsTemplateAIAgent(payload));

                if (generateUrsTemplateAIAgent.fulfilled.match(genResult)) {
                    dispatch(showSuccess("success"));
                    onGenerate(ursTemplate);
                } else {
                    let errorMsg = "error";
                    if (genResult.payload && (genResult.payload as any).kind === "http") {
                        const problem = (genResult.payload as any).problem;
                        if (problem) errorMsg = problem.title;
                    }
                    setServerError(errorMsg);
                }
            } else {
                let errorMsg = "error";
                if (prepareResult.payload && (prepareResult.payload as any).kind === "http") {
                    const problem = (prepareResult.payload as any).problem;
                    if (problem) errorMsg = problem.title;
                }
                setServerError(errorMsg);
            }
        } catch (error) {
            dispatch(showError("error"));
        } finally {
            setSaving(false);
            setProgressStage(null);
        }
    };


    return (
        <Dialog open fullWidth maxWidth="sm">
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "grey.200",
                    color: "text.primary",
                    fontWeight: 700,
                    gap: 1,
                }}
            >
                <FindInPageIcon color="info" />
                <span>Generuj wymagania z załącznika</span>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {/* Ustawienia */}
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}
                    >
                        <FormControl sx={{ flex: 1, minWidth: 260 }} margin="dense" size="small">
                            <InputLabel id="urs-type-label">Typ wymagań</InputLabel>
                            <Select
                                labelId="urs-type-label"
                                label="Typ wymagań"
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                            >
                                {REQUIREMENT_TYPES.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>
                                        {t.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl sx={{ width: 140 }} margin="dense" size="small">
                            <InputLabel id="app-language-label">Język</InputLabel>
                            <Select
                                labelId="app-language-label"
                                value={language}
                                onChange={(e) => setLanguage(e.target.value as "PL" | "EN")}
                                label="Język"
                            >
                                <MenuItem value="PL">PL</MenuItem>
                                <MenuItem value="EN">EN</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Divider />
                    {saving && progressStage && (
                        <Alert severity="info">
                            {progressStage === "PREPARING"
                                ? "Trwa przygotowanie AGENTA…"
                                : "Trwa generowanie URS…"}
                        </Alert>
                    )}
                    {/* Lista plików */}
                    {hasBlobs ? (
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Na podstawie którego pliku wygenerować wymagania?
                            </Typography>

                            <List
                                dense
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                }}
                            >
                                {blobs.map((b) => {
                                    const fileName = b.fileMeta?.fileName ?? b.blobId;
                                    const contentType = b.fileMeta?.contentType;

                                    return (
                                        <ListItemButton
                                            key={b.blobId}
                                            selected={b.blobId === selectedBlobId}
                                            onClick={() => {
                                                setSelectedBlobId(b.blobId);
                                                setFileSelectionError(false);
                                                setServerError("");
                                            }}
                                            sx={{
                                                "&.Mui-selected": { bgcolor: "action.selected" },
                                            }}
                                        >
                                            <Radio
                                                checked={b.blobId === selectedBlobId}
                                                onChange={() => {
                                                    setSelectedBlobId(b.blobId);
                                                    setFileSelectionError(false);
                                                    setServerError("");
                                                }}
                                                value={b.blobId}
                                                size="small"
                                            />
                                            <ListItemAvatar sx={{ minWidth: 40 }}>
                                                {fileIcon(contentType)}
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={fileName}
                                                secondary={""}
                                                primaryTypographyProps={{ variant: "body2" }}
                                                secondaryTypographyProps={{ variant: "caption" }}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>

                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                Wybrany plik:{" "}
                                <strong>{selectedBlob?.fileMeta?.fileName ?? "—"}</strong>
                            </Typography>
                        </Box>
                    ) : (
                        <Alert severity="warning">
                            <Localized id="">
                                <span>_no attachments available for this document</span>
                            </Localized>
                        </Alert>
                    )}

                    {serverError && (
                        <Alert sx={{ mt: 1 }} onClose={() => setServerError("")} severity="error">
                            <Localized id={serverError}>
                                <span>Server error</span>
                            </Localized>
                        </Alert>
                    )}
                    {fileSelectionError && !selectedBlobId && hasBlobs && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            Wybierz plik, żeby móc wygenerować wymagania.
                        </Alert>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <GenerateButton loading={saving} onClick={handleConfirmGenerateClick} />
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </Dialog>
    );
};
